import { Fragment, useEffect, useRef } from 'react';
import { Container, Flex, Box } from 'theme-ui';
import { useTranslation } from 'next-i18next';
import { observer } from 'mobx-react-lite';
import styles from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockModal/ProductGalleryBlockModal.styles';
import { IParsedImage } from '@components/typescript';
import { TProductGallerySlide } from '@features/cms/typescript';
import Button from '@components/Button/Button';
import Icon from '@components/icons/Icon';
import ProductState from '@features/categories/mobx/state/ProductState';
import ProductCard from '@components/ProductCardNew/ProductCard';
import RenderMedia from '@utils/RenderMedia';
import { BLANK_VIDEO_OBJECT } from '@features/cms/const';
type PropsType = {
  isActive: boolean;
  slide: TProductGallerySlide<IParsedImage>;
  onClose: () => void;
};
const ProductGalleryBlockModal = ({
  isActive,
  slide,
  onClose
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    products,
    image,
    video
  } = slide;
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Escape key + focus trap - WCAG 2.1.2 No Keyboard Trap
  useEffect(() => {
    if (!isActive || !modalRef.current) return;
    previousActiveElementRef.current = document.activeElement as HTMLElement;
    const getFocusableElements = (): HTMLElement[] => {
      if (!modalRef.current) return [];
      const focusableSelectors = ['button:not([disabled])', 'a[href]', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', '[role="button"]:not([aria-disabled="true"])'].join(', ');
      const elements = Array.from(modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors));
      return elements.filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && el.tabIndex !== -1;
      });
    };
    const focusFirstInModal = () => {
      const elements = getFocusableElements();
      if (elements.length > 0) elements[0].focus();
    };

    // Po otwarciu modala (np. klik w zdjęcie) focus wchodzi do modala po zakończeniu animacji (.28s)
    const focusTimeout = setTimeout(focusFirstInModal, 320);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        previousActiveElementRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      const elements = getFocusableElements();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      const isFocusInsideModal = modalRef.current?.contains(document.activeElement);
      const currentIndex = elements.indexOf(document.activeElement as HTMLElement);

      // Gdy focus jest poza modalem (np. po kliknięciu w zdjęcie), pierwszy Tab wciąga go do modala
      if (!isFocusInsideModal) {
        e.preventDefault();
        if (e.shiftKey) last.focus();else first.focus();
        return;
      }
      if (e.shiftKey) {
        if (currentIndex === 0 || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (currentIndex === elements.length - 1 || document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElementRef.current && document.activeElement === document.body) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isActive, onClose]);
  return <div ref={modalRef} sx={styles.modal(slide && isActive)} role="dialog" aria-modal="true" aria-hidden={!isActive}>
      <div sx={styles.modalCloseButton}>
        <Button variant="iconOnly" onClick={onClose} aria-label={t('close')}>
          <Icon icon="Close2" size={['16px', '16px', '21px', '21px', '24px']} />
        </Button>
      </div>
      <div>
        <Container variant="fluid">
          <Flex sx={styles.modalContentFlexContainer}>
            <Box sx={styles.modalContentImageBox}>
              <div sx={styles.imageContainer}>
                {RenderMedia(image.image, video || BLANK_VIDEO_OBJECT, true, {
                image: image.image,
                blur: image?.blur || BLANK_VIDEO_OBJECT
              }, '', true)}
              </div>
            </Box>
            <Box sx={styles.modalContentProductsBox}>
              <div sx={styles.modalContentOverflow}>
                <div sx={styles.modalContentProductsGrid}>
                  {products.map(product => {
                  const productState = new ProductState(product);
                  return <Fragment key={productState.id}>
                        <div sx={styles.modalContentProduct}>
                          <ProductCard product={productState} fromModal />
                        </div>
                      </Fragment>;
                })}
                </div>
              </div>
            </Box>
          </Flex>
        </Container>
      </div>
    </div>;
};
export default observer(ProductGalleryBlockModal);